import * as React from "react";
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";
import { Circle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { ModalHeader } from "@/components/ui/modal-header";
import { Button } from "@/components/ui/button";
import { useResetOnOpen } from "@/hooks/use-reset-on-open";
import { cn } from "@/lib/utils";
import type { Group } from "@/api/types";

export interface ChangeGroupModalProps {
  open: boolean;
  productName: string;
  groups: Group[];
  activeGroupId: number;
  onCancel: () => void;
  onSubmit: (groupId: number) => void;
}

function ChangeGroupModal({
  open,
  productName,
  groups,
  activeGroupId,
  onCancel,
  onSubmit,
}: ChangeGroupModalProps) {
  const [selectedGroupId, setSelectedGroupId] = React.useState<number | null>(
    null,
  );

  useResetOnOpen(open, () => {
    setSelectedGroupId(null);
  });

  const activeGroup = groups.find((g) => g.id === activeGroupId);
  const availableGroups = groups.filter((g) => g.id !== activeGroupId);
  const isSingleGroup = availableGroups.length === 0;

  function handleSubmit() {
    if (selectedGroupId === null) return;
    onSubmit(selectedGroupId);
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) onCancel();
      }}
    >
      <DialogContent className="max-w-xl p-0">
        <ModalHeader title="Change Group" />

        <div className="flex flex-col gap-5 px-6 py-5">
          <section className="flex flex-col gap-3">
            <div className="flex items-center gap-3">
              <span className="whitespace-nowrap text-sm font-medium text-secondary-900">
                Active Group
              </span>
              <div className="h-px flex-1 bg-primary-300" />
            </div>
            <DialogDescription className="text-sm text-black-primary">
              You are currently viewing <strong>{productName}</strong> using the{" "}
              <strong>{activeGroup?.name}</strong> group.
            </DialogDescription>
          </section>

          <section className="flex flex-col gap-3">
            <div className="flex items-center gap-3">
              <span className="whitespace-nowrap text-sm font-medium text-secondary-900">
                Available Groups
              </span>
              <div className="h-px flex-1 bg-primary-300" />
            </div>
            {isSingleGroup ? (
              <p className="text-sm text-black-primary">
                Your active group is the only group you are a member of.
              </p>
            ) : (
              <>
                <p className="text-sm text-black-primary">
                  To change your active group, select a group from the list
                  below. If you apply this change, your view will be refreshed
                  based on the permissions of that group.
                </p>
                <RadioGroupPrimitive.Root
                  value={selectedGroupId?.toString() ?? ""}
                  onValueChange={(value) => setSelectedGroupId(Number(value))}
                  className="mt-1 flex flex-col gap-3"
                  aria-label="Available groups"
                >
                  {availableGroups.map((group) => {
                    const itemId = `change-group-${group.id}`;
                    return (
                      <div key={group.id} className="flex items-center gap-2">
                        <RadioGroupPrimitive.Item
                          id={itemId}
                          value={group.id.toString()}
                          className={cn(
                            "aspect-square h-4 w-4 rounded-full border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                            "border-secondary-400 bg-white-primary",
                            "data-[state=checked]:border-blue-1 data-[state=checked]:text-blue-1",
                          )}
                        >
                          <RadioGroupPrimitive.Indicator className="flex items-center justify-center">
                            <Circle className="h-2.5 w-2.5 fill-current" />
                          </RadioGroupPrimitive.Indicator>
                        </RadioGroupPrimitive.Item>
                        <label
                          htmlFor={itemId}
                          className="cursor-pointer text-sm leading-none text-black-primary"
                        >
                          {group.name}
                        </label>
                      </div>
                    );
                  })}
                </RadioGroupPrimitive.Root>
              </>
            )}
          </section>
        </div>

        <DialogFooter>
          <Button variant="outlined" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            variant="solid"
            disabled={selectedGroupId === null}
            onClick={handleSubmit}
          >
            Submit Group Change
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export { ChangeGroupModal };
